import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Truck, MapPin, Shield, Clock, Edit2, Check, X, Loader2, Inbox, DollarSign, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import ReviewSection from "@/components/ReviewSection";
import { logger } from "@/utils/logger";

const TransporterDashboard = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState("profile");
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (activeTab === "bookings" && profile) fetchBookings();
  }, [activeTab, profile]);

  const fetchBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const res = await api.get("/transporter/bookings");
      setBookings(res.data.bookings || []);
    } catch (err) { logger.error("Fetch error:", err); } finally {
      setBookingsLoading(false);
    }
  }, []);

  const handleBookingAction = async (bookingId, status, quotedPrice) => {
    try {
      const body = { status };
      if (quotedPrice) body.quoted_price = quotedPrice;
      await api.patch(`/transporter/bookings/${bookingId}`, body);
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update booking");
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get("/transporter/profile");
      setProfile(response.data.profile);
      setEditData(response.data.profile);
    } catch (error) {
      logger.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/transporter/profile", editData);
      toast.success("Profile updated");
      setProfile(editData);
      setEditing(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: "bg-[#F59E0B]/10 text-[#F59E0B]",
      approved: "bg-[#22C55E]/10 text-[#22C55E]",
      rejected: "bg-[#EF4444]/10 text-[#EF4444]"
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${styles[status] || styles.pending}`}>
        {status === "approved" ? "Verified" : status === "rejected" ? "Rejected" : "Pending Verification"}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3B82F6] animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
            <Truck className="w-10 h-10 text-[#3B82F6]" />
          </div>
          <h1 className="text-3xl text-[#EDEDED] mb-4">Become a Transporter</h1>
          <p className="text-[#A1A1AA] mb-8">
            Join Breedling's network of verified animal transporters
          </p>
          <Link to="/transporter/register" className="btn-gold inline-flex items-center gap-2">
            <Truck className="w-4 h-4" /> Register Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
              <Truck className="w-8 h-8 text-[#3B82F6]" />
            </div>
            <div>
              <h1 className="text-2xl text-[#EDEDED]">{profile.business_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={profile.verification_status} />
                {profile.is_verified && (
                  <span className="flex items-center gap-1 text-xs text-[#22C55E]">
                    <Shield className="w-3 h-3" /> Verified Transporter
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" /> Edit Profile
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "profile", label: "Profile" },
            { id: "bookings", label: "Bookings", count: bookings.filter(b => b.status === "pending").length },
            { id: "reviews", label: "Reviews" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id ? "bg-[#3B82F6] text-white" : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <>
            {/* Pending Notice */}
            {profile.verification_status === "pending" && (
          <div className="glass-card p-4 mb-6 border-[#F59E0B]/30">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#F59E0B]" />
              <div>
                <p className="text-[#EDEDED] font-medium">Verification Pending</p>
                <p className="text-xs text-[#A1A1AA]">
                  Your application is being reviewed. This typically takes 24-48 hours.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Info */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact & Location */}
          <div className="glass-card p-6">
            <h3 className="text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#3B82F6]" /> Contact & Location
            </h3>
            
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">Business Name</label>
                  <input
                    type="text"
                    value={editData.business_name || ""}
                    onChange={(e) => setEditData({...editData, business_name: e.target.value})}
                    className="input-minimal w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#71717A] mb-1">City</label>
                    <input
                      type="text"
                      value={editData.location_city || ""}
                      onChange={(e) => setEditData({...editData, location_city: e.target.value})}
                      className="input-minimal w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#71717A] mb-1">State</label>
                    <input
                      type="text"
                      value={editData.location_state || ""}
                      onChange={(e) => setEditData({...editData, location_state: e.target.value})}
                      className="input-minimal w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editData.contact_phone || ""}
                    onChange={(e) => setEditData({...editData, contact_phone: e.target.value})}
                    className="input-minimal w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">Email</label>
                  <input
                    type="email"
                    value={editData.contact_email || ""}
                    onChange={(e) => setEditData({...editData, contact_email: e.target.value})}
                    className="input-minimal w-full"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-[#71717A]">Location</p>
                  <p className="text-[#EDEDED]">
                    {profile.location_city && profile.location_state 
                      ? `${profile.location_city}, ${profile.location_state}`
                      : "Not specified"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#71717A]">Phone</p>
                  <p className="text-[#EDEDED]">{profile.contact_phone || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-[#71717A]">Email</p>
                  <p className="text-[#EDEDED]">{profile.contact_email || "Not specified"}</p>
                </div>
                {profile.years_experience && (
                  <div>
                    <p className="text-xs text-[#71717A]">Experience</p>
                    <p className="text-[#EDEDED]">{profile.years_experience} years</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Licensing & Insurance */}
          <div className="glass-card p-6">
            <h3 className="text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#3B82F6]" /> Licensing & Insurance
            </h3>
            
            <div className="space-y-3">
              <div>
                <p className="text-xs text-[#71717A]">License Number</p>
                <p className="text-[#EDEDED]">
                  {profile.license_number 
                    ? `${profile.license_number} (${profile.license_state || "N/A"})`
                    : "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#71717A]">Insurance Provider</p>
                <p className="text-[#EDEDED]">{profile.insurance_provider || "Not provided"}</p>
              </div>
              <div>
                <p className="text-xs text-[#71717A]">Policy Number</p>
                <p className="text-[#EDEDED]">{profile.insurance_policy_number || "Not provided"}</p>
              </div>
              <div className="pt-2">
                <p className="text-xs text-[#71717A]">Animal Welfare Pledge</p>
                <p className={profile.welfare_pledge_accepted ? "text-[#22C55E]" : "text-[#EF4444]"}>
                  {profile.welfare_pledge_accepted ? "Accepted" : "Not accepted"}
                </p>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="glass-card p-6">
            <h3 className="text-lg text-[#EDEDED] mb-4">Species Transported</h3>
            <div className="flex flex-wrap gap-2">
              {profile.species_served?.length > 0 ? (
                profile.species_served.map(species => (
                  <span key={species} className="px-3 py-1 bg-[#3B82F6]/10 text-[#3B82F6] rounded-full text-sm capitalize">
                    {species}
                  </span>
                ))
              ) : (
                <p className="text-[#71717A] text-sm">No species specified</p>
              )}
            </div>

            <h3 className="text-lg text-[#EDEDED] mt-6 mb-4">Equipment</h3>
            <div className="flex flex-wrap gap-2">
              {profile.equipment_types?.length > 0 ? (
                profile.equipment_types.map(eq => (
                  <span key={eq} className="px-3 py-1 bg-[#27272A] text-[#A1A1AA] rounded-full text-sm">
                    {eq}
                  </span>
                ))
              ) : (
                <p className="text-[#71717A] text-sm">No equipment specified</p>
              )}
            </div>
          </div>

          {/* Service Areas */}
          <div className="glass-card p-6">
            <h3 className="text-lg text-[#EDEDED] mb-4">Service Areas</h3>
            <div className="flex flex-wrap gap-1">
              {profile.regions_served?.length > 0 ? (
                profile.regions_served.map(state => (
                  <span key={state} className="px-2 py-1 bg-[#3B82F6]/10 text-[#3B82F6] rounded text-xs font-medium">
                    {state}
                  </span>
                ))
              ) : (
                <p className="text-[#71717A] text-sm">No service areas specified</p>
              )}
            </div>

            <h3 className="text-lg text-[#EDEDED] mt-6 mb-4">Pricing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-[#71717A]">Rate per Mile</p>
                <p className="text-[#EDEDED] text-lg">
                  {profile.base_rate_per_mile ? `$${profile.base_rate_per_mile.toFixed(2)}` : "Contact for quote"}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#71717A]">Minimum Charge</p>
                <p className="text-[#EDEDED] text-lg">
                  {profile.minimum_charge ? `$${profile.minimum_charge.toFixed(2)}` : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <div className="glass-card p-6 mt-6">
            <h3 className="text-lg text-[#EDEDED] mb-3">About</h3>
            <p className="text-[#A1A1AA]">{profile.bio}</p>
          </div>
        )}

        {/* Edit Actions */}
        {editing && (
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => {
                setEditing(false);
                setEditData(profile);
              }}
              className="btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold flex-1 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        )}
        </>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div>
            {bookingsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" /></div>
            ) : bookings.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Inbox className="w-12 h-12 text-[#3F3F46] mx-auto mb-3" />
                <p className="text-[#A1A1AA]">No booking requests yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map(b => (
                  <div key={b.booking_id} className="glass-card p-5" data-testid={`booking-${b.booking_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            b.status === "pending" ? "bg-[#F59E0B]/10 text-[#F59E0B]" :
                            b.status === "accepted" ? "bg-[#22C55E]/10 text-[#22C55E]" :
                            b.status === "quoted" ? "bg-[#3B82F6]/10 text-[#3B82F6]" :
                            b.status === "completed" ? "bg-[#22C55E]/10 text-[#22C55E]" :
                            "bg-[#EF4444]/10 text-[#EF4444]"
                          }`}>{b.status}</span>
                          <span className="text-xs text-[#71717A] capitalize">{b.request_type}</span>
                        </div>
                        <p className="text-[#EDEDED]">{b.requester_name || "Unknown"}</p>
                      </div>
                      <span className="text-xs text-[#52525B]">{new Date(b.created_at).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-xs text-[#71717A]">From</p>
                        <p className="text-[#A1A1AA]">{[b.pickup_city, b.pickup_state].filter(Boolean).join(", ") || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#71717A]">To</p>
                        <p className="text-[#A1A1AA]">{[b.delivery_city, b.delivery_state].filter(Boolean).join(", ") || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#71717A]">Species</p>
                        <p className="text-[#A1A1AA] capitalize">{b.species || "—"} ({b.animal_count})</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#71717A]">Est. Distance</p>
                        <p className="text-[#A1A1AA]">{b.estimated_distance_miles ? `${b.estimated_distance_miles} mi` : "—"}</p>
                      </div>
                    </div>
                    
                    {b.notes && <p className="text-sm text-[#71717A] mb-3">{b.notes}</p>}
                    
                    {b.status === "pending" && (
                      <div className="flex gap-2 pt-3 border-t border-white/5">
                        <button onClick={() => handleBookingAction(b.booking_id, "accepted")} className="btn-gold text-xs px-3 py-1.5 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Accept
                        </button>
                        <button onClick={() => {
                          const price = prompt("Enter quoted price ($):");
                          if (price) handleBookingAction(b.booking_id, "quoted", parseFloat(price));
                        }} className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> Quote
                        </button>
                        <button onClick={() => handleBookingAction(b.booking_id, "declined")} className="text-xs px-3 py-1.5 rounded-lg bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20">
                          <X className="w-3 h-3 inline" /> Decline
                        </button>
                      </div>
                    )}
                    {b.status === "accepted" && (
                      <div className="pt-3 border-t border-white/5">
                        <button onClick={() => handleBookingAction(b.booking_id, "completed")} className="btn-gold text-xs px-3 py-1.5 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Mark Completed
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && profile && (
          <div className="glass-card p-6">
            <ReviewSection targetType="transporter" targetId={profile.transporter_id} user={user} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TransporterDashboard;
